import { useEffect } from 'react';
import * as modelViewer from '@google/model-viewer';
import i18n from '@/i18n/i18n';

interface UseARButtonVisibilityOptions {
  modelViewerRef: React.RefObject<modelViewer.ModelViewerElement>;
  isDesktop: boolean;
}

type NavigatorWithBrave = Navigator & {
  brave?: { isBrave?: () => Promise<boolean> | boolean };
};

/**
 * iPhone, iPod, and iPad, including iPadOS when it reports a desktop Safari UA.
 * Same signals model-viewer uses.
 */
function isIosOrIpadOs(): boolean {
  const ua = navigator.userAgent;
  const classicIos = /iPad|iPhone|iPod/.test(ua) && !('MSStream' in window);
  const ipadOsDesktopSite = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return classicIos || ipadOsDesktopSite;
}

/**
 * Brave iOS appends "Brave" to the Safari UA and exposes navigator.brave.isBrave().
 * Either signal is enough; other browsers expose neither.
 */
async function isBraveBrowser(): Promise<boolean> {
  if (/\bBrave\b/.test(navigator.userAgent)) return true;
  const brave = (navigator as NavigatorWithBrave).brave;
  if (!brave?.isBrave) return false;
  try {
    return Boolean(await brave.isBrave());
  } catch {
    return false;
  }
}

const SAFARI_FALLBACK_DELAY_MS = 1500;

/**
 * Brave iOS cannot open USDZ in AR Quick Look, so hand the page to Safari instead.
 * The x-safari-https:// scheme exists on iOS 17+; on older iOS the page stays visible
 * and the user is told to open it in Safari manually.
 */
function openCurrentPageInSafari(): void {
  const { href } = window.location;
  if (!/^https?:\/\//.test(href)) return;

  let leftPage = false;
  const onHide = () => {
    if (document.visibilityState === 'hidden') leftPage = true;
  };
  document.addEventListener('visibilitychange', onHide);
  window.setTimeout(() => {
    document.removeEventListener('visibilitychange', onHide);
    if (!leftPage && document.visibilityState === 'visible') {
      window.alert(i18n.t('productViewer.braveArOpenInSafari'));
    }
  }, SAFARI_FALLBACK_DELAY_MS);

  window.location.href = `x-safari-${href}`;
}

/**
 * Custom hook to manage AR button visibility in model-viewer
 * Hides AR button on desktop, shows on mobile/VR if AR is supported
 * Listens to model-viewer load events to update visibility
 */
export function useARButtonVisibility({
  modelViewerRef,
  isDesktop,
}: UseARButtonVisibilityOptions) {
  useEffect(() => {
    const modelViewer = modelViewerRef.current;
    if (!modelViewer) return;

    // Only hide on desktop, show on mobile/VR if AR is supported
    const updateARButtonVisibility = () => {
      const arButton = modelViewer.shadowRoot?.querySelector(
        '[part="default-ar-button"]'
      ) as HTMLElement;
      if (arButton) arButton.style.display = (isDesktop || !modelViewer.canActivateAR) ? 'none' : 'flex';
    };

    // Try immediately
    updateARButtonVisibility();

    // Listen for model-viewer load (model loaded, AR availability might change)
    modelViewer.addEventListener('load', updateARButtonVisibility);

    return () => {
      modelViewer.removeEventListener('load', updateARButtonVisibility);
    };
  }, [modelViewerRef, isDesktop]);

  useBraveIosArButton({ modelViewerRef, isDesktop });
}

const BRAVE_AR_POLL_MS = 500;

/**
 * Brave iOS only. model-viewer never enables Quick Look there, so its AR slot stays
 * display:none via :not(.enabled). Do not add .enabled — the library removes it and
 * reports AR as failed. Polls because the <model-viewer> may mount, remount (key change),
 * or finish AR mode selection after this effect runs.
 */
function useBraveIosArButton({ modelViewerRef, isDesktop }: UseARButtonVisibilityOptions) {
  useEffect(() => {
    if (isDesktop || !isIosOrIpadOs()) return;

    let cancelled = false;
    let pollId: number | undefined;
    let braveViewer: modelViewer.ModelViewerElement | null = null;
    let braveButton: HTMLElement | null = null;
    let braveSlot: HTMLElement | null = null;

    const handleClick = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      openCurrentPageInSafari();
    };

    const detachClick = () => {
      braveButton?.removeEventListener('click', handleClick);
      braveButton = null;
    };

    const clearOverride = () => {
      detachClick();
      braveSlot?.style.removeProperty('display');
      braveSlot = null;
      braveViewer = null;
    };

    const apply = () => {
      const viewer = modelViewerRef.current;
      if (!viewer) {
        if (braveViewer) clearOverride();
        return;
      }
      if (viewer !== braveViewer) clearOverride();

      if (!viewer.ar || viewer.canActivateAR) {
        clearOverride();
        return;
      }

      const slot = viewer.shadowRoot?.querySelector('.slot.ar-button') as HTMLElement | null;
      const arButton = viewer.shadowRoot?.querySelector(
        '[part="default-ar-button"]'
      ) as HTMLElement | null;
      if (!slot || !arButton) return;

      braveViewer = viewer;
      slot.style.setProperty('display', 'block');
      arButton.style.display = 'flex';
      braveSlot = slot;

      if (braveButton !== arButton) {
        detachClick();
        arButton.addEventListener('click', handleClick);
        braveButton = arButton;
      }
    };

    void (async () => {
      const brave = await isBraveBrowser();
      if (cancelled || !brave) return;
      apply();
      pollId = window.setInterval(apply, BRAVE_AR_POLL_MS);
    })();

    return () => {
      cancelled = true;
      if (pollId !== undefined) window.clearInterval(pollId);
      clearOverride();
    };
  }, [modelViewerRef, isDesktop]);
}
